import { useState, useRef, useEffect } from 'react';
import { PARAMETROS_SIMULACAO, TIPOS_PARCELA, LIMITES } from '../../../utils/constantes';
import { formatCurrency, calculateAge, getEnrollmentIdsByCommercialTierType, validateCpf } from '../../../utils/formatters';
import { useAlertHelpers } from '../../layout/Alert/AlertHelpers';
import { simularProposta } from '../../../services/api';

export const useSimulationLogic = (propertyData, clientData, creditAnalysisData, walletData, onSimulationComplete, showWalletCard, options = {}) => {
	const { isPropostaIdeal = false, isMRV = false } = options;
	const [isSimulating, setIsSimulating] = useState(false);
	const [error, setError] = useState(null);
	const { showError, showWarning } = useAlertHelpers();

	const isMRVRef = useRef(isMRV);
	useEffect(() => { isMRVRef.current = isMRV; }, [isMRV]);

	const formatarResultadoSimulacao = (result) => {
		const formattedResult = { ...result };

		if (formattedResult.proposta?.parcelas) {
			let sinalCount = 0;

			formattedResult.proposta.parcelas = result.proposta.parcelas
				.sort((a, b) => new Date(a.dataVencimento) - new Date(b.dataVencimento))
				.map(parcela => {
					const data = new Date(parcela.dataVencimento);
					const dataFormatada = `${data.getDate().toString().padStart(2, '0')}/${(data.getMonth() + 1).toString().padStart(2, '0')}/${data.getFullYear()}`;

					let tipoDescricao;

					if (TIPOS_PARCELA[parcela.tipo]?.toLowerCase() === 'sinal') {
						if (parcela.atoComplementar) {
							// Sinal (3) e Carta de Crédito (7) ocupam a mesma posição (o ato): quando a Carta
							// é aplicada e não cobre o ato inteiro, essa parcela complementar nasce logo em
							// seguida com a diferença — rotulamos explicitamente em vez de contar como sinal comum.
							tipoDescricao = 'Ato Complementar (Carta de Crédito)';
						} else if (sinalCount === 0) {
							tipoDescricao = clientData?.pagadoria ? 'Sinal Ato +Comissão' : 'Sinal Ato';
						} else {
							tipoDescricao = `Sinal ${sinalCount}`;
						}
						sinalCount++;
					} else {
						tipoDescricao = TIPOS_PARCELA[parcela.tipo];
					}

					return {
						...parcela,
						dataFormatada,
						tipoDescricao,
						valorFormatado: formatCurrency(parcela.valorNominal)
					};
				});
		}

		return formattedResult;
	};

	const executarSimulacaoProposta = async (requestData) => {
		setIsSimulating(true);
		setError(null);

		try {
			console.log('Enviando request de simulação:', requestData);

			const result = await simularProposta(requestData);

			const formattedResult = formatarResultadoSimulacao(result);

			onSimulationComplete(formattedResult);
			return formattedResult;
		} catch (error) {
			console.error('Erro ao processar simulação:', error);
			showError(error.message, { title: 'Erro', duration: 6000 });
			return null;
		} finally {
			setIsSimulating(false);
		}
	}; 
	
	const validarCompradores = (buyers, ehPropostaBase) => {
		if (!buyers?.length || buyers.length > LIMITES.COMPRADORES) {
			showWarning(`O número máximo de compradores é ${LIMITES.COMPRADORES}.`, { title: 'Atenção', duration: 4000 });
			return false;
		}

		if (!buyers.every(buyer => buyer.income > 0) && ehPropostaBase) {
			showWarning('Por favor, preencha a renda de todos os compradores.', { title: 'Atenção', duration: 4000 });
			return false;
		}

		for (const buyer of buyers) {
			if (!buyer.birthDate) {
				showWarning('Por favor, selecione uma data de nascimento válida para todos os compradores.', { title: 'Atenção', duration: 4000 });
				return false;
			}
			const idade = calculateAge(buyer.birthDate);
			if (idade < 18 || idade > 80) {
				showWarning('Todos os compradores devem ter entre 18 e 80 anos de idade.', { title: 'Atenção', duration: 4000 });
				return false;
			}
		}
		return true;
	};

	const validarPagadoria = (pagadoria, valorComissao, ehPropostaBase) => {
		if (pagadoria && valorComissao <= 0 && ehPropostaBase) {
			showWarning('Por favor, insera o valor da comissão.', { title: 'Atenção', duration: 4000 });
			return false;
		}
		return true;
	};

	const validarPropriedade = (selectedUnitId) => {
		if (!selectedUnitId) {
			showWarning('Por favor, selecione uma unidade válida para continuar com a simulação.', { title: 'Atenção', duration: 4000 });
			return false;
		}
		return true;
	};

	const validarAnaliseCredito = (tipoSimulacaoCredito, valorFinanciado) => {
		if (tipoSimulacaoCredito != 1) {
			showWarning('Este tipo de simulação não está disponível no momento. Por favor, selecione outra modalidade de simulação para continuar.', { title: 'Atenção', duration: 4000 });
			return false;
		}

		if (valorFinanciado <= 0) {
			showWarning('O campo Valor Financiado deve conter valor', { title: 'Atenção', duration: 4000 });
			return false;
		}
		return true;
	};

	const validarCarteira = (walletData, isPropostaIdeal) => {
		if (!isPropostaIdeal) {
			const valorAto = walletData?.signals?.[0]?.Valor ?? 0;

			// Entrada Facilitada não bloqueia mais Carta de Crédito — decisão da área de negócios
			// (Pagadoria sim, mas essa combinação já é impedida na UI, ver WalletCard.cartaDeCreditoDesabilitada).

			if (walletData?.cartaDeCredito && valorAto <= 0) {
				showWarning('Por favor, informe o valor da Carta de Crédito no campo do ato.', { title: 'Atenção', duration: 4000 });
				return false;
			}

			if (walletData?.signals?.length) {
				for (const signal of walletData.signals) {
					if (signal.Valor <= 100) {
						showWarning(`Conforme a proposta ideal, o valor mínimo do sinal deve ser maior que ${formatCurrency(100)}`, { title: 'Atenção', duration: 4000 });
						return false;
					}
				}
			}
			if (walletData?.balaDePrata &&
				walletData?.qtdParcelasMaxima !== undefined &&
				walletData?.monthlyInstallmentsCount > walletData?.qtdParcelasMaxima) {
				showWarning(`A quantidade de parcelas mensais deve estar entre 1 e ${walletData?.qtdParcelasMaxima}.`, { title: 'Atenção', duration: 4000 });
				return false;
			}
		}
		return true;
	};

	const validarDataIntermediaria = (intermediates) => {
		if (intermediates &&
			Array.isArray(intermediates) &&
			intermediates.length > 0) {

			const currentDate = new Date();
			let targetMonth = currentDate.getMonth() + 2;
			let targetYear = currentDate.getFullYear();

			if (targetMonth > 11) {
				targetMonth -= 12;
				targetYear += 1;
			}

			const datasValidas = intermediates.every(item =>
				item?.AnoIntermediaria > targetYear ||
				(item?.AnoIntermediaria === targetYear && item?.MesIntermediaria > targetMonth)
			);

			if (!datasValidas) {
				showWarning('Data inválida cadastrada na tabela de intermediaria.', { title: 'Atenção', duration: 4000 });
				return false;
			}
		}
		return true;
	};

	const validarCpfProponentes = (cpfProponentes) => {
		if (cpfProponentes && cpfProponentes.trim() !== '') {
			if (!validateCpf(cpfProponentes)) {
				showWarning('CPF do proponente é inválido. Por favor, verifique e corrija o CPF informado.', { title: 'Atenção', duration: 4000 });
				return false;
			}
		}
		return true;
	};
	
	const validarSimulacao = () => {
		const ehPropostaBase = walletData?.idPropostaAnaliseCredito === PARAMETROS_SIMULACAO.ID_PROPOSTA_BASE;
		const cpfProponentes = clientData?.buyers?.[0]?.cpf || '';

		// Execute each validation function in sequence
		return validarCompradores(clientData?.buyers, ehPropostaBase) &&
			validarPagadoria(clientData?.pagadoria, clientData?.valorComissao, ehPropostaBase) &&
			validarPropriedade(propertyData?.selectedUnitId) &&
			validarAnaliseCredito(creditAnalysisData?.tipoSimulacaoCredito, creditAnalysisData?.valorFinanciado) &&
			validarCarteira(walletData, isPropostaIdeal) &&
			validarDataIntermediaria(walletData?.intermediates) &&
			validarCpfProponentes(cpfProponentes);
	};

	const validarEExecutarSimulacao = async () => {
		if (!validarSimulacao()) {
			return;
		}

		console.log("Proposta ideal:", isPropostaIdeal);
		const bloqueioSinal = (walletData?.signals && walletData?.signals.length > 0);
		const bloqueioParcelamento = walletData?.qtdParcelaIdeal !== undefined && walletData?.qtdParcelaIdeal !== walletData?.monthlyInstallmentsCount;
		const subsidyValue = parseFloat(creditAnalysisData?.subsidyValue || 0) + parseFloat(creditAnalysisData?.otherSubsidiesValue || 0);
		const comissaoPagadoria = clientData?.valorComissao > 0 ? clientData?.valorComissao / 100 : 0;
		const IdEnquadramento = getEnrollmentIdsByCommercialTierType(propertyData?.commercialTierType, creditAnalysisData?.rendaProponentes);
		const rating = walletData?.rating;

		let signals = walletData?.signals;
		let monthlyInstallmentsCount = walletData?.monthlyInstallmentsCount;

		if (isPropostaIdeal) {
			signals = [{ Valor: 0 }];
			monthlyInstallmentsCount = 50;
		}

		const simulationRequest = {
			"IdEnquadramentoApurado": IdEnquadramento,
			"RendaTotalProponentes": parseFloat(creditAnalysisData?.rendaProponentes || 0),
			"Prazo": parseInt(PARAMETROS_SIMULACAO.PRAZO),
			"JurosEfetivo": parseFloat(PARAMETROS_SIMULACAO.JUROS_EFETIVO) / 100,
			"ValorParcelaLiberado": parseFloat((creditAnalysisData?.rendaProponentes || 0) * 0.3),
			"ValorFinanciamentoLiberado": parseFloat(creditAnalysisData?.valorFinanciado || 0),
			"SistemaAmortizacao": parseInt(PARAMETROS_SIMULACAO.SISTEMA_AMORTIZACAO),
			"ValorSubsidioLiberado": parseFloat(subsidyValue) || 0,
			"ValorFgtsApurado": parseFloat(creditAnalysisData?.fgtsValue || 0),
			"ValorCupomLiberado": parseFloat(walletData?.couponValue || 0),
			"CartaDeCredito": walletData?.cartaDeCredito || false,
			"RatingGerencial": walletData?.balaDePrata || false,
			"JornadaPlano": walletData?.planoFacil || false,
			"EntradaFacilitada": walletData?.entradaFacilitada || false,
			"EmCashAtivo": PARAMETROS_SIMULACAO.EM_CASH,
			"Pagadoria": clientData?.pagadoria || false,
			"ComissaoPagadoria": comissaoPagadoria,
			"Sinais": signals || [],
			"Marca": isMRVRef.current ? PARAMETROS_SIMULACAO.MARCA_MRV : PARAMETROS_SIMULACAO.MARCA_SENSIA,
			"Rating": rating,
			"ValorParcelaMensal": parseFloat(walletData?.monthlyInstallmentValue || '0'),
			"QuantidadeParcelasMensais": parseInt(monthlyInstallmentsCount || '0'),
			"Intermediarias": walletData?.intermediates || [],
			"IdUnidade": propertyData?.selectedUnitId,
			"IdPropostaAnaliseCredito": walletData?.idPropostaAnaliseCredito || "00000000-0000-0000-0000-000000000000",
			"Bloqueios": {
				"Sinal": bloqueioSinal,
				"Parcelamento": bloqueioParcelamento
			},
			"CPFProponentes": clientData?.buyers?.[0]?.cpf || '',
		};

		await executarSimulacaoProposta(simulationRequest);
	};

	return {
		isSimulating,
		error,
		handleSimulate: validarEExecutarSimulacao
	};
};